import React, { useCallback, useContext, useMemo } from 'react'
import styled from 'styled-components'
import config from 'constants/config'
import { useAppSelector } from 'hooks/reduxHooks'
import { pluralizeToString } from 'lib/string/pluralize'
import useBedbankDiscount from 'hooks/useBedbankDiscount'
import LuxPlusPriceStack from 'luxPlus/components/LuxPlusPriceStack'
import LuxPlusPriceGuard from 'luxPlus/components/LuxPlusPriceGuard'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { getIsLuxPlusLPPEnabled } from 'luxPlus/selectors/featureToggle'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import { getDisplayTotalPrice } from 'lib/hotels/bedbankUtils'
import Group from 'components/utils/Group'
import TextButton from 'components/Luxkit/Button/TextButton'
import PriceRowPrice from 'components/Luxkit/PricePoints/PriceRowPrice'
import PriceRowTaxesAndFees from 'components/Luxkit/PricePoints/PriceRowTaxesAndFees'
import PriceRowPriceCaption from 'components/Luxkit/PricePoints/PriceRowPriceCaption'
import OfferPriceDetailsRow from 'components/Common/PriceDetails/OfferPriceDetailsRow'
import PriceRowAgentHubCommission from 'agentHub/components/PriceRowAgentHubCommission'
import BedbankTwinShare from 'components/OfferList/OfferListTiles/Support/BedbankTwinShare'
import PriceRowValueDiscountWithCaption from 'components/Luxkit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import BusinessTravellerOfferCreditsTextLink from 'businessTraveller/components/offer-credits/BusinessTravellerOfferCreditsTextLink'
import BedbankMarginText from '../BedbankMargin'
import { isRoomsInvalid } from 'lib/search/searchUtils'
import ModalContext from 'contexts/ModalContext'
import BedbankRoomDetailsModal from './Modals/BedbankRoomDetailsModal'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import useBedbankFlightPrice from 'hooks/OfferPage/useBedbankFlightPrice'
import { getCheckoutDate } from 'selectors/offerPage/offerPageSelectors'
import { selectIsBedbankFlightBookingEnabled } from 'selectors/flightsSelectors'
import { sumUpOccupancies } from 'lib/offer/occupancyUtils'

const StyledTextButton = styled(TextButton)`
  display: flex;
  flex: 1;
`

interface Props {
  promotions?: App.BedbankPromotion;
  rate: App.BedbankRate;
  bundleRate?: App.BedbankRate;
  rooms: Array<App.Occupants>;
  offer: App.BedbankOffer | App.BedbankOfferSummary;
  checkIn?: string;
  luxPlusLPPEnabled: boolean;
  packageView: App.BedbankOfferPackageView;
  detailsModalMode: 'room-and-package' | 'room-only';
  isFlightsSelected: boolean;
  checkInDate: string;
  checkOutDate: string;
  flightOrigin: App.Airport;
}

function BedbankPackageTileCheapestPricing(props: Props) {
  const {
    rate,
    bundleRate,
    promotions,
    rooms,
    offer,
    checkIn,
    luxPlusLPPEnabled,
    packageView,
    detailsModalMode,
    isFlightsSelected,
    checkInDate,
    checkOutDate,
    flightOrigin,
  } = props

  const showModal = useContext(ModalContext)
  const offerPageDispatch = useOfferPageDispatch()

  const {
    propertyFees = 0,
    taxesAndFees,
  } = rate.totals

  const hotelPrice = rate.totals.inclusive
  const hotelMemberPrice = rate.totals.memberInclusive ?? 0
  const bundlePrice = bundleRate?.totals.inclusive || hotelPrice
  const bundlePropertyFees = bundleRate?.totals.propertyFees || propertyFees

  const flightPrice = useBedbankFlightPrice(offer, {
    rooms,
    startDate: checkInDate,
    endDate: checkOutDate,
    originAirportCode: flightOrigin?.code,
  })

  const totalPrices = getDisplayTotalPrice({
    hotelPrice: hotelPrice + propertyFees,
    hotelMemberPrice: hotelMemberPrice > 0 ? hotelMemberPrice + propertyFees : 0,
    hotelValue: rate.value,
    bundlePrice: bundlePrice + bundlePropertyFees,
    bundleValue: bundleRate?.value,
    checkIn,
    flightPrice,
    rooms,
    displayPricingAsPerPerson: offer.displayPricingAsPerPerson,
  })

  const saleUnit = offer.displayPricingAsPerPerson ? 'person' : 'total'
  const isPromoRate = rate.id === promotions?.id

  const margin = rate?.margin
  const marginAud = rate?.marginAud
  const supplier = rate?.supplier
  const isSpoofed = useAppSelector(state => state.auth.account.isSpoofed)
  const showMarginInSpoofMode = useAppSelector(state => state.system.showMarginInSpoofMode)
  const displayMargin = Boolean(margin && marginAud && supplier && isSpoofed && showMarginInSpoofMode)
  const discount = useBedbankDiscount(rate?.discount, rate?.memberDiscount)
  const invalidRoomSelection = !rooms || isRoomsInvalid(rooms)

  const bundleMargin = bundleRate?.margin
  const bundleMarginAud = bundleRate?.marginAud
  const bundleSupplier = bundleRate?.supplier
  const displayBundleMargin = Boolean(bundleMargin && bundleMarginAud && bundleSupplier && isSpoofed && showMarginInSpoofMode)

  const isBedbankFlightBookingEnabled = useAppSelector(selectIsBedbankFlightBookingEnabled)
  const occupancyCount = useMemo(() => sumUpOccupancies(rooms), [rooms])
  const flightsEnabled = !!offer.flights.flightsEnabled && isBedbankFlightBookingEnabled && occupancyCount < 10

  const showFlightPrice = isFlightsSelected && flightsEnabled && !!flightPrice

  const memberPrice = showFlightPrice ? totalPrices.totals.flightBundleMemberPrice : totalPrices.totals.memberPrice
  const price = showFlightPrice ? totalPrices.totals.flightBundlePrice : totalPrices.totals.price
  const originalPrice = showFlightPrice ? totalPrices.original.flightBundlePrice : totalPrices.original.price
  const value = showFlightPrice ? totalPrices.totals.flightBundleValue : totalPrices.totals.value

  const viewOptionsLabel = useMemo(() => {
    if (!checkIn) return 'Check availability'
    return 'Select room'
  }, [checkIn])

  const onShowRoomDetails = useCallback(() => {
    if (detailsModalMode === 'room-and-package') {
      // Reset LPC room type for Single Property pages
      offerPageDispatch({ type: OfferPageActions.setRoomType, room: undefined })
      offerPageDispatch({ type: OfferPageActions.setBedbankRoomId, bedbankRoomId: packageView.package.roomId })
      offerPageDispatch({ type: OfferPageActions.toggleFilter, filter: 'roomDetails' })
    } else {
      showModal(<BedbankRoomDetailsModal packageView={packageView} />, true)
    }
  }, [detailsModalMode, offerPageDispatch, packageView, showModal])

  const onViewOptionsClick = useCallback(() => {
    // Reset LPC room type for Single Property pages
    offerPageDispatch({ type: OfferPageActions.setRoomType, room: undefined })
    offerPageDispatch({ type: OfferPageActions.setBedbankRoomId, bedbankRoomId: packageView.package.roomId })

    if (invalidRoomSelection) {
      return offerPageDispatch({ type: OfferPageActions.toggleFilter, filter: 'capacity' })
    }

    if (!checkIn) {
      return offerPageDispatch({ type: OfferPageActions.toggleFilter, filter: 'checkin' })
    }

    offerPageDispatch({ type: OfferPageActions.toggleFilter, filter: 'packageSelection' })
  }, [checkIn, invalidRoomSelection, offerPageDispatch, packageView.package.roomId])

  return (
    <Group direction="vertical" gap={16}>
      <Group direction="vertical" gap={2}>
        <PriceRowPriceCaption>
          {pluralizeToString('night', rate.nights)}{showFlightPrice && ' + flights'}{checkIn ? ' from' : ''}
        </PriceRowPriceCaption>
        <LuxPlusPriceGuard
          showMemberPrice={(memberPrice ?? 0) > 0 && luxPlusLPPEnabled}
          fallbackPrice={<PriceRowPrice
            size="M"
            price={price}
            saleUnit={saleUnit}
          />}
        >
          <LuxPlusPriceStack
            size="M"
            price={price}
            memberPrice={memberPrice}
            saleUnit={saleUnit}
          />
        </LuxPlusPriceGuard>
        {offer.displayPricingAsPerPerson && <BedbankTwinShare rooms={rooms} />}
        {!isPromoRate && <PriceRowTaxesAndFees />}
        <PriceRowAgentHubCommission
          size="M"
          productType={offer.productType}
          offerLocations={[]}
          offerId={offer.id}
          packageId={rate.id}
          offerTotal={originalPrice}
          kind="plain"
        />
        {config.businessTraveller.currentAccountMode === 'business' && <BusinessTravellerOfferCreditsTextLink
          type="exact"
          offer={offer}
          totalCost={originalPrice}
          numberOfNights={rate.nights}
          roomCount={rooms.length}
        />}
        {!!discount && <PriceRowValueDiscountWithCaption
          size="M"
          originalValue={value}
        />}
        {!isPromoRate && <OfferPriceDetailsRow
          trigger="price-row"
          triggerSize="M"
          offerPage
          offer={offer}
          rooms={rooms}
          propertyFees={propertyFees}
          taxesAndFees={taxesAndFees}
          hotelPrice={price}
          hotelMemberPrice={memberPrice}
          showMemberPrice={luxPlusLPPEnabled && memberPrice > 0}
          duration={rate.nights}
          hasMobilePromotion={rate.mobilePromotion}
        />}
        {!showFlightPrice && displayMargin && <BedbankMarginText
          margin={margin!}
          marginAud={marginAud!}
          supplier={supplier!}
          type="bedbank_exact"
        />}
        {showFlightPrice && displayBundleMargin && <BedbankMarginText
          margin={bundleMargin!}
          marginAud={bundleMarginAud!}
          supplier={bundleSupplier!}
          type="bedbank_exact_accom"
        />}
      </Group>
      <Group direction="horizontal" gap={8}>
        <StyledTextButton size="large" kind="secondary" onClick={onShowRoomDetails}>View details</StyledTextButton>
        <StyledTextButton size="large" kind="primary" onClick={onViewOptionsClick}>{viewOptionsLabel}</StyledTextButton>
      </Group>
    </Group>
  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  luxPlusLPPEnabled: getIsLuxPlusLPPEnabled(appState),
  isFlightsSelected: state.flights,
  checkInDate: state.checkInDate,
  checkOutDate: getCheckoutDate(state),
  flightOrigin: state.airport,
}))(BedbankPackageTileCheapestPricing)
